/**
 * ActionBar Component
 * Bottom action buttons (Cancel, Print, Accept)
 */

import { i18n } from '../utils/i18n.js';

export class ActionBar {
  constructor({ container, onCancel, onViewPdf, onPrint, onAccept, onFinishAndPrint, settings = {} }) {
    this.container = container;
    this.onCancel = onCancel;
    this.onViewPdf = onViewPdf;
    this.onPrint = onPrint;
    this.onAccept = onAccept;
    this.onFinishAndPrint = onFinishAndPrint;
    this.settings = {
      showCancelButton: settings.showCancelButton !== false,
      showViewPDFButton: settings.showViewPDFButton || false,
      showSignPaperButton: settings.showSignPaperButton !== false,
      showFinishButton: settings.showFinishButton !== false,
      showFinishAndSignPaperButton: settings.showFinishAndSignPaperButton || false
    };
  }

  render({
    canAccept = false,
    canCancel = true,
    acceptLabelKey = 'actionBar.accept',
    canFinishAndPrint = canAccept
  } = {}) {
    const acceptLabel = i18n.t(acceptLabelKey);
    const buttons = [];
    
    const iconSize = 'w-5 h-5';
    const btnBase = 'flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 sm:px-4 sm:py-2.5 text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

    // Cancel button
    if (this.settings.showCancelButton) {
      buttons.push(`
        <button 
          class="${btnBase} text-white bg-red-600 hover:bg-red-700 focus:ring-red-500 disabled:bg-gray-300 disabled:cursor-not-allowed" 
          id="wsp-btn-cancel"
          ${!canCancel ? 'disabled' : ''}
          title="${i18n.t('actionBar.cancel')}"
        >
          <svg class="${iconSize} flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
          <span class="hidden sm:inline">${i18n.t('actionBar.cancel')}</span>
        </button>
      `);
    }

    // View PDF button
    if (this.settings.showViewPDFButton) {
      buttons.push(`
        <button class="${btnBase} text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-primary-500" id="wsp-btn-view-pdf" title="${i18n.t('actionBar.viewPdf')}">
          <svg class="${iconSize} flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
            <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/>
          </svg>
          <span class="hidden sm:inline">${i18n.t('actionBar.viewPdf')}</span>
        </button>
      `);
    }

    // Print button
    if (this.settings.showSignPaperButton) {
      buttons.push(`
        <button class="${btnBase} text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:ring-primary-500" id="wsp-btn-print" title="${i18n.t('actionBar.print')}">
          <svg class="${iconSize} flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clip-rule="evenodd"/>
          </svg>
          <span class="hidden sm:inline">${i18n.t('actionBar.print')}</span>
        </button>
      `);
    }

    // Finish button
    if (this.settings.showFinishButton) {
      buttons.push(`
        <button 
          class="${btnBase} text-white bg-green-600 hover:bg-green-700 focus:ring-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed" 
          id="wsp-btn-accept"
          ${!canAccept ? 'disabled' : ''}
          title="${acceptLabel}"
        >
          <svg class="${iconSize} flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          <span class="hidden sm:inline">${acceptLabel}</span>
        </button>
      `);
    }

    // Finish and Print button
    if (this.settings.showFinishAndSignPaperButton) {
      buttons.push(`
        <button 
          class="${btnBase} text-white bg-green-600 hover:bg-green-700 focus:ring-green-500 disabled:bg-gray-300 disabled:cursor-not-allowed" 
          id="wsp-btn-finish-print"
          ${!canFinishAndPrint ? 'disabled' : ''}
          title="${i18n.t('actionBar.finishAndPrint')}"
        >
          <svg class="${iconSize} flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
          <svg class="${iconSize} flex-shrink-0 -ml-1" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clip-rule="evenodd"/>
          </svg>
          <span class="hidden sm:inline">${i18n.t('actionBar.finishAndPrint')}</span>
        </button>
      `);
    }

    this.container.innerHTML = `
      <div class="flex items-center justify-between gap-2 sm:gap-3 px-2 py-2 sm:px-4 sm:py-3">
        ${buttons.join('')}
      </div>
    `;

    this.attachEventListeners();
  }

  attachEventListeners() {
    const cancelBtn = this.container.querySelector('#wsp-btn-cancel');
    const viewPdfBtn = this.container.querySelector('#wsp-btn-view-pdf');
    const printBtn = this.container.querySelector('#wsp-btn-print');
    const acceptBtn = this.container.querySelector('#wsp-btn-accept');
    const finishPrintBtn = this.container.querySelector('#wsp-btn-finish-print');

    if (cancelBtn) cancelBtn.addEventListener('click', () => this.onCancel());
    if (viewPdfBtn) viewPdfBtn.addEventListener('click', () => this.onViewPdf());
    if (printBtn) printBtn.addEventListener('click', () => this.onPrint());
    if (acceptBtn) acceptBtn.addEventListener('click', () => this.onAccept());
    if (finishPrintBtn) finishPrintBtn.addEventListener('click', () => this.onFinishAndPrint());
  }
}
